import type { FormikHelpers } from 'formik';
import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import type { CloudUploadScope } from 'api/apiCloudflare';
import * as apiService from 'api/apiServices';
import { useAuth } from 'context/AuthContext';
import { hasUserAccess } from 'utils/helpers/access/hasUserAccess';
import { FilesContainer } from 'modules/files';
import { parseCloudFiles, stringifyCloudFiles } from 'modules/files';
import type { ServiceEditType } from '../../types';

interface ServiceFormFilesProps {
    setFieldValue: FormikHelpers<ServiceEditType>['setFieldValue'];
    values: ServiceEditType;
    ensureSaved: () => Promise<CloudUploadScope | false>;
}

const ServiceFormFiles = ({ setFieldValue, values, ensureSaved }: ServiceFormFilesProps) => {
    const { auth } = useAuth();
    const { t } = useTranslation(['Services', 'FileNavigator', 'common']);
    const serviceId = Number(values?.ID || 0);
    const serviceIdRef = useRef(serviceId);
    const canEdit = hasUserAccess(auth?.user, 'prodEdit');
    const files = parseCloudFiles(values.servFiles);

    useEffect(() => {
        serviceIdRef.current = serviceId;
    }, [serviceId]);

    const handleFilesChange = async (nextFiles: ReturnType<typeof parseCloudFiles>) => {
        const nextJson = stringifyCloudFiles(nextFiles);
        const currentId = serviceIdRef.current;
        if (currentId <= 0) {
            setFieldValue('servFiles', nextJson);
            return true;
        }
        const stat = await apiService.saveFiles(currentId, nextJson);
        if (!stat) return false;
        setFieldValue('servFiles', nextJson);
        return true;
    };

    const handleEnsureEntity = async (): Promise<CloudUploadScope | false> => {
        const scope = await ensureSaved();
        if (!scope) return false;
        const createdId = Number(scope.serviceId || 0);
        if (createdId > 0) serviceIdRef.current = createdId;
        return scope;
    };

    return (
        <FilesContainer
            files={files}
            scope={{ serviceId }}
            canEdit={canEdit}
            addButtonLabel={t('btnAddFiles')}
            uploadingLabel={t('common:buttons.uploading')}
            saveFirstMessage={t('messSaveServiceFirst')}
            errorMessage={t('common:messError')}
            uploadSuccessMessage={t('FileNavigator:messDownloadSuccess')}
            onFilesChange={handleFilesChange}
            onEnsureEntity={serviceId <= 0 ? handleEnsureEntity : undefined}
        />
    );
};

export default ServiceFormFiles;
